import { BrowseEventsInLocationSelector } from '@/components/BrowseEventsInLocationSelector';
import { EventsBucket } from '@/components/EventsBucket';
import { filterUnactionableEvents } from '@/shared/utils/filterUnactionableEvents';
import { IconCategoryBrowse } from '@/components/IconCategoryBrowse';
import { RootState } from '@/redux/store';
import { locationChange } from '@/redux/location';
import classNames from 'classnames';
import { useEffect, useMemo, useState, useTransition } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { BrowseTabs } from '@/components/BrowseTabs/BrowseTabs';
import { ALL_TAB, FOR_YOU, TABS_CONFIG } from '@/constants/tabConfig';
import styles from './BrowseContainer.module.scss';
import { getLastRecentSearchTerms, getLastRecentViewsList } from '@/components/utils/utils';
import { interleaveTwoArrays } from '@/components/utils/arrayUtils';
import { selectLocale } from '@/redux/app';
import { useAppSelector } from '@/redux/hooks';
import { sdkRequest } from '@eventbrite/legacy-infra.http';
import { getPromotedEventsOptions } from './promotedUtils';
import { EventInteraction, aggregateContext } from '@/utils/tracking';
import { useTracking } from '@/tracking';
import { ForYou } from '@/components/ForYou/ForYou';
import { useClientAuth } from '@/common/useUserPublicId';
import { useLinearPaidMode } from '@/bsp/config';
import type { bboxShape } from '@eventbrite/consumer-search.discover-utils/types';
import { DestinationEvent } from '@eventbrite/consumer-search.event-renderer';

const PROMOTED_EVENTS_RANKS = [1, 4, 9];

interface BrowseContainerProps extends PropsFromRedux {
    userId: string;
}

export function convertBboxToSearchFormat(
    bboxData: bboxShape | null | undefined,
): { top: number; left: number; bottom: number; right: number } | undefined {
    if (!bboxData?.sw?.lat || !bboxData?.ne?.lat) return undefined;

    return {
        top: bboxData.ne.lat,
        left: bboxData.sw.lng,
        bottom: bboxData.sw.lat,
        right: bboxData.ne.lng,
    };
}

export const BrowseContainer = (props: BrowseContainerProps) => {
    const { location, setUserLocation, userId } = props;
    const locale = useAppSelector(selectLocale);
    const [searchDataEvents, setSearchDataEvents] = useState<{ events: DestinationEvent[] }>({
        events: [],
    });
    // we don't want to fetch the events until we have finished getting the location
    // By default after SSr the location will be null, we don't want to fire the call to get default evetns just yet
    // By using isInitialLoad, we wait for that call to be over or failed, and after it is over, we use the response to fetch the events corresponding to the location
    const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [currentTab, setCurrentTab] = useState<string>(ALL_TAB);
    const [isPending, startTransition] = useTransition();

    const { authState, publicUserId } = useClientAuth();

    // Linear paid-weight sweep Orion experiment: when enrolled, forward the sweep
    // arm as `linearPaidMode` (alongside `scoreMode: 'linear'`) so the search
    // service ranks with the linear ranker using that arm's paid weight.
    const { paidMode, isLoading: isLinearPaidModeLoading } = useLinearPaidMode();

    useEffect(() => {
        if (publicUserId) {
            setCurrentTab(FOR_YOU);
        }
    }, [publicUserId]);

    useEffect(() => {
        const fetchUserLocation = async () => {
            try {
                const response = await fetch('/home/api/geo/place_from_request/');
                if (!response.ok) {
                    throw new Error('Failed to fetch user location');
                }
                const { place } = await response.json();
                setUserLocation(place);
            } catch (e) {
                console.error('Error fetching user location:', e);
            } finally {
                setIsInitialLoad(false);
            }
        };
        fetchUserLocation();
    }, []);

    const fetchEventsForTab = async (tabKey: string) => {
        setIsLoading(true);

        if (tabKey === FOR_YOU) {
            setSearchDataEvents({ events: [] });
            setIsLoading(false);
            return;
        }

        try {
            const placeId = location?.placeId;
            const bbox = convertBboxToSearchFormat(location?.bbox);

            const organicEventsReq = fetch(`/home/api/search/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    placeId,
                    tab: tabKey,
                    bbox,
                    ...(paidMode ? { scoreMode: 'linear', linearPaidMode: paidMode } : {}),
                }),
            });
            const promotedEventsReq = sdkRequest(
                '/promoted/events/',
                getPromotedEventsOptions(location, TABS_CONFIG[currentTab], {
                    recentSearches: getLastRecentSearchTerms(10),
                    recentViews: getLastRecentViewsList(10),
                }),
                '/home',
            );

            const [organicEventsRes, promotedEventsRes] = await Promise.all([
                organicEventsReq.catch(() => {
                    throw new Error('Failed to fetch organic events');
                }),
                promotedEventsReq.catch(() => []),
            ]);

            const organicData = await organicEventsRes.json();
            const promotedData = promotedEventsRes;

            startTransition(() => {
                if (promotedData.length > 0) {
                    const interleavedEvents = interleaveTwoArrays(
                        organicData.events,
                        promotedData,
                        PROMOTED_EVENTS_RANKS,
                    );
                    setSearchDataEvents({ events: interleavedEvents as DestinationEvent[] });
                    setIsLoading(false);
                } else {
                    setSearchDataEvents(organicData);
                    setIsLoading(false);
                }
            });
        } catch (error) {
            console.error(`Error fetching search data events for tab: ${tabKey}`, error);
            setSearchDataEvents({ events: [] });
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // Hold until Orion settles too, so a treatment visitor whose gate resolves
        // after mount isn't read as control and sent the base paid weight.
        if (!isInitialLoad && !isLinearPaidModeLoading) {
            fetchEventsForTab(currentTab);
        }
    }, [location.placeId, location.bbox, currentTab, isInitialLoad, isLinearPaidModeLoading]);

    const handleTabChange = (tab: string) => {
        startTransition(() => {
            setCurrentTab(tab);
        });
    };

    // Rank against the same list EventsBucket actually renders.
    // EventsBucket drops unactionable events (not on-sale, non-recurrent) before display, so ranking against
    // the raw searchDataEvents makes absolute_rank/rank index a longer list than the user sees and skew positions.
    const renderedEvents = useMemo(
        () => filterUnactionableEvents(searchDataEvents?.events),
        [searchDataEvents],
    );

    const getTrackingContext = ({ id, event }: EventInteraction) =>
        aggregateContext({
            id,
            event,
            events: renderedEvents,
            userId,
            locale,
            tabKey: currentTab,
            location,
        });
    // Local tracker fans each interaction out to both log_engagement and PicoX
    // (product_location: 'homepage') from one deduped, batched stream.
    const { ref } = useTracking<HTMLDivElement>(getTrackingContext);

    return (
        <div className={styles.root} data-testid="browse-section" ref={ref}>
            <IconCategoryBrowse />
            <hr className={styles.border} />
            <div className={classNames(styles.horizontalGutters, styles.largeMaxWidth)}>
                <BrowseEventsInLocationSelector location={location} />
            </div>
            <hr className={styles.border} />
            <div className={classNames(styles.horizontalGutters, styles.largeMaxWidth)}>
                <BrowseTabs onTabChange={handleTabChange} selectedTab={currentTab} />
                {currentTab !== FOR_YOU ? (
                    <EventsBucket
                        loadingState={{ isFeLoading: isLoading || isPending }}
                        events={searchDataEvents?.events || []}
                        currentTab={currentTab}
                        authState={authState}
                        publicUserId={publicUserId}
                    />
                ) : (
                    <ForYou />
                )}
            </div>
        </div>
    );
};

const _mapStateToProps = (state: RootState) => ({
    // isAuthenticated: Boolean(state.user.isAuthenticated),
    // isMobile: state.env.isMobile,
    // tld: state.env?.localeInfo?.tld,
    // tabKey: state.content.browseState?.tabKey,
    // loadingState: state.app.loadingState,
    location: state.location,
    // seasonalContent: state.content.seasonal,
    // featureFlags: state.app.featureFlags,
    // useNewHeader: state.header.forceSpecialStyles,
});

const _mapDispatchToProps = {
    // fetchEventsForTab,
    // fetchUserSavedEvents,
    setUserLocation: locationChange,
};

export const connector = connect(_mapStateToProps, _mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const ConnectedBrowseContainer = connector(BrowseContainer);
